import React, { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { DelayedLoader, defaultTheme, THEME } from '@mummssoftware/common-ui';
import { defaultIDGContent } from '@mummssoftware/idg';
import {
  Pim,
  Observation,
  auth,
  ClearScripts,
  Workflow,
  Scheduler,
  Reporting,
  ServiceConfig,
} from '@mummssoftware/utils';
import { getSiteId } from '@mummssoftware/utils/utils';
import { formatDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import { LoggerType, Logger, DEFAULT_LOG_LEVEL } from '@mummssoftware/logger';
import { initThemeListener } from '@mummssoftware/pim-messenger';
import { DEFAULT_LOCALE } from './i18n';
import Providers from './Providers';
import { Context, ContextProvider } from './context';
import App from './containers/App';
import {
  Config,
  DashboardType,
  LegacyEnvWidgetConfig,
} from './containers/Context/types';
import {
  getKeycloakConfigUserId,
  mergeWidgetConfigFromApi,
  logWidgetConfigSource,
  resolveWidgetConfigFromSources,
  getWidgetConfigCacheKey,
  readWidgetConfigCache,
  writeWidgetConfigCache,
  areWidgetConfigsEqual,
} from './containers/Context/widgetConfig';
import ContextProviders from './containers/Context/contexts/ContextProviders';
import {
  patientDashboard,
  clinicalDashboard,
  FORM_NAME,
} from './containers/Context/constants';
// trigger rebuild

// trigger rebuild

type DashboardProps = {
  keycloak: Keycloak.KeycloakInstance;
  patientNumber?: string;
  theme?: string | null;
  agency?: string | null;
  locale?: keyof mumms.translations;
  /*  controls whether certain elements (i.e. the top bar) display */
  embedded?: boolean;
  /* optional instance of pim, otherwise instantiates its own */
  pim?: Pim;
  /* pass currentUser if already retrieved to avoid excessive api calls- AS OF NOW WOULD NOT HAPPEN */
  currentUser?: mumms.User;
  /* pass patient if already retrieved to avoid excessive api calls- AS OF NOW WOULD NOT HAPPEN */
  patient?: mumms.patient;
  // widget config
  config: Config;
  // logger
  logger?: LoggerType;
};

interface DashboardState {
  context: Context | null;
  theme?: string | null;
}

const setPatientError = (res: Response, patientNumber?: string) => ({
  patientError: {
    status: res.status,
    statusText: res.statusText,
    patientNumber,
  },
});

const setPatient = (patient: mumms.patient | undefined | null) => {
  if (!patient) {
    return undefined;
  }
  return patient;
};

const getPatientNumber = (patientNumberProp?: string) =>
  patientNumberProp ?? window.location.pathname.substring(1).split('/')[0];

const Content = React.memo(
  ({ context, embedded }: DashboardState & { embedded?: boolean }) => {
    if (context === null) {
      return <DelayedLoader />;
    }
    return (
      <ContextProvider value={context}>
        <Suspense fallback={<DelayedLoader />}>
          {/* TODO: better name */}
          <ContextProviders context={context}>
            <App
              currentUser={context.currentUser}
              embedded={embedded}
              patient={context.patient}
              patientError={context.patientError}
              keycloak={context.keycloak}
              logout={context.keycloak.logout}
              pim={context.pim}
              config={context.config}
              dashboardType={context.dashboardType}
            />
          </ContextProviders>
        </Suspense>
      </ContextProvider>
    );
  },
);

Content.displayName = 'MainContent';
// trigger build
class Dashboard extends React.PureComponent<DashboardProps, DashboardState> {
  pim: Pim;

  observation: Observation;

  clearscripts: ClearScripts;

  workflow: Workflow;

  scheduler: Scheduler;

  reporting: Reporting;

  serviceConfig: ServiceConfig;

  agency?: string;

  logger?: LoggerType;

  siteId: string | undefined;

  /** Unsaved patient dashboard layout; blocks background widget-config refresh. */
  widgetConfigLayoutDirty = false;

  /** Deferred {@link refreshWidgetConfigInBackground} while {@link widgetConfigLayoutDirty}. */
  pendingWidgetConfigBackgroundRefresh: {
    cacheKey: string;
    cachedConfig: Config;
  } | null = null;

  constructor(props: DashboardProps) {
    super(props);
    const { keycloak, agency } = props;
    const query = new URLSearchParams(window.location.search);
    const siteId = getSiteId();

    // Resolve agency: props -> URL ?agency= -> keycloak token -> localStorage
    let resolvedAgency: string | undefined;
    if (agency) {
      resolvedAgency = agency;
    } else {
      const adminAgency = query.get('agency') as string;
      if (adminAgency) {
        resolvedAgency = adminAgency;
      } else {
        const parsedAgency = auth.getLdapDn(keycloak.idTokenParsed);
        resolvedAgency = auth.getAgency(parsedAgency) as string;
      }
    }

    if (resolvedAgency) {
      localStorage.setItem('agency-support', resolvedAgency);
    } else {
      resolvedAgency = localStorage.getItem('agency-support') || undefined;
    }

    this.agency = resolvedAgency;

    if (siteId) {
      this.siteId = siteId;
    }

    this.pim =
      props.pim ||
      new Pim({
        url: process.env.REACT_APP_PIM_BASEURL as string,
        pgRestUrl: process.env.REACT_APP_PGREST_PROXY,
        keycloak,
        agency: this.agency,
      });
    this.observation = new Observation({
      url: process.env.REACT_APP_OBSERVATION_URL as string,
      keycloak,
      agency: this.agency,
    });
    this.clearscripts = new ClearScripts({
      url: process.env.REACT_APP_CLEARSCRIPTS_BASEURL as string,
      keycloak,
      agency: this.agency,
    });
    this.workflow = new Workflow({
      url: process.env.REACT_APP_WORKFLOW_URL as string,
      keycloak,
      agency: this.agency,
    });
    this.reporting = new Reporting({
      url: process.env.REACT_APP_REPORTING_URL as string,
      keycloak,
      agency: this.agency,
    });
    this.serviceConfig = new ServiceConfig({
      url: process.env.REACT_APP_CONFIG_SERVICE_URL as string,
      keycloak,
      agency: this.agency,
    });

    this.scheduler = new Scheduler({
      url: (process.env.REACT_APP_PIM_BASEURL as string) as string,
      keycloak,
      agency: this.agency,
      siteId: siteId as string,
    });

    this.state = {
      context: null,
      theme: null,
    };
  }
  // update the dashboard theme

  onDashboardThemeChange = (updatedTheme: string) => {
    this.setState((prevState) => ({
      context: prevState.context,
      theme: updatedTheme,
    }));
  };

  setWidgetConfigLayoutDirty = (dirty: boolean) => {
    const wasDirty = this.widgetConfigLayoutDirty;
    this.widgetConfigLayoutDirty = dirty;
    if (wasDirty && !dirty && this.pendingWidgetConfigBackgroundRefresh) {
      const pending = this.pendingWidgetConfigBackgroundRefresh;
      this.pendingWidgetConfigBackgroundRefresh = null;
      this.refreshWidgetConfigInBackground(
        pending.cacheKey,
        pending.cachedConfig,
      );
    }
  };

  /**
   * Callback invoked after a successful dashboard layout PUT
   * ({@link usePersistPatientDashboardWidgetLayout} → `saveWidgetConfig`).
   *
   * **Why this lives on the root Dashboard class:** `config` is owned here (boot GET /
   * cache / env fallback) and passed down through App. After save, the server response
   * becomes the new baseline so the grid no longer depends on session `added` / `removed`
   * deltas once {@link onPersistSuccess} clears them in DashboardComponent.
   *
   * **What it updates**
   * - `context.config.widgets[patientDashboard|clinicalDashboard].layout` only —
   *   palette is not sent on PUT and is left unchanged.
   * - `configSource` → `'api'` (save implies config-service is authoritative).
   * - sessionStorage widget-config cache (`writeWidgetConfigCache`) when agency + user id
   *   (+ patientNumber for patient charts) are available, so reload and background refresh
   *   see the saved layout for that dashboard context.
   *
   * **Dirty flag:** clears {@link widgetConfigLayoutDirty} so
   * {@link refreshWidgetConfigInBackground} may run again.
   *
   * @param layout — rows returned by PUT, already mapped via {@link normalizeApiWidget}.
   * @param dashboard — PUT target (`patient` | `clinical`).
   */
  onDashboardLayoutSaved = (
    layout: mumms.WidgetConfig[],
    dashboard: mumms.WidgetConfigDashboard,
  ) => {
    const dashboardKey =
      dashboard === 'clinical' ? 'clinicalDashboard' : 'patientDashboard';
    this.setState((prevState) => {
      if (!prevState.context?.config?.widgets) {
        return prevState;
      }
      const nextConfig: Config = {
        ...prevState.context.config,
        configSource: 'api',
        widgets: {
          ...prevState.context.config.widgets,
          [dashboardKey]: {
            ...prevState.context.config.widgets[dashboardKey],
            layout,
          },
        },
      };
      const cacheKey = getWidgetConfigCacheKey(
        this.agency,
        getKeycloakConfigUserId(this.props.keycloak),
        getPatientNumber(this.props.patientNumber),
      );
      if (cacheKey) {
        writeWidgetConfigCache(cacheKey, nextConfig);
      }
      return {
        ...prevState,
        context: {
          ...prevState.context,
          config: nextConfig,
        },
      };
    });
    this.setWidgetConfigLayoutDirty(false);
  };

  /** Query params for GET /api/widgets/config (user override, patient episode lookup). */
  buildWidgetConfigParams = (configUserId?: string | null) => {
    const patientNumber = getPatientNumber(this.props.patientNumber);
    const params = {
      ...(configUserId ? { userId: configUserId } : {}),
      ...(patientNumber ? { patientNumber } : {}),
      ...(this.agency ? { agency: this.agency } : {}),
    };
    return Object.keys(params).length > 0 ? params : undefined;
  };

  // instantiate new listener
  getInitialData = async () => {
    const {
      currentUser: externalUser,
      patientNumber: patientNumberProp,
      keycloak,
    } = this.props;
    const patientNumber = getPatientNumber(patientNumberProp);
    initThemeListener(this.onDashboardThemeChange);

    const isMummsAdmin = auth.isMummsAdmin(keycloak.tokenParsed as any);

    const params = new URLSearchParams(window.location.search);
    // should be the meeting ID
    // strange spelling because of a backend issue
    const idgMeetingID = params.get('idgmeeting');
    // format('MM-DD-YYYY') --- 2021-04-20 05:00:00
    const from = formatDateMMDDYYYY(params.get('from'));
    const to = formatDateMMDDYYYY(params.get('to'));
    // can be true
    const idgReviewComplete = params.get('idgmeetingdone');
    const isIdgMeeting = !!idgMeetingID && !!from && !!to;
    const isIdgMeetingComplete =
      !!idgMeetingID && !!from && !!to && idgReviewComplete === 'true';

    const envWidgetConfig = window.env?.widgetconfig as
      | LegacyEnvWidgetConfig
      | undefined;
    const configUserId = getKeycloakConfigUserId(keycloak);
    const widgetConfigCacheKey = getWidgetConfigCacheKey(
      this.agency,
      configUserId,
      patientNumber,
    );
    const cachedWidgetConfig = widgetConfigCacheKey
      ? readWidgetConfigCache(widgetConfigCacheKey)
      : null;
    const widgetConfigParams = this.buildWidgetConfigParams(configUserId);

    let currentUser: mumms.User | Response;
    let patientResponse: mumms.patient | Response | undefined;
    let widgetconfig: Config | null;
    let configSource: Config['configSource'];

    if (cachedWidgetConfig) {
      [currentUser, patientResponse] = await Promise.all([
        !externalUser
          ? this.pim.getUserInfo(this.agency || '')
          : Promise.resolve(externalUser),
        patientNumber
          ? this.pim.getPatientHeader(patientNumber, this.agency || '')
          : undefined,
      ]);
      widgetconfig = cachedWidgetConfig;
      configSource = 'cache';
    } else {
      let widgetConfigResponse: mumms.WidgetsConfigApiResponse | Response;
      [currentUser, patientResponse, widgetConfigResponse] = await Promise.all([
        !externalUser
          ? this.pim.getUserInfo(this.agency || '')
          : Promise.resolve(externalUser),
        patientNumber
          ? this.pim.getPatientHeader(patientNumber, this.agency || '')
          : undefined,
        this.serviceConfig.getWidgetConfig(widgetConfigParams),
      ]);
      const resolved = resolveWidgetConfigFromSources(
        widgetConfigResponse,
        envWidgetConfig,
      );
      widgetconfig = resolved.config;
      configSource = resolved.configSource ?? undefined;
      if (configSource === 'env') {
        console.warn(
          '[widget-config] config-service failed; using env fallback',
          widgetConfigResponse instanceof Response
            ? widgetConfigResponse.status
            : undefined,
        );
      } else if (
        configSource === 'api' &&
        widgetConfigCacheKey &&
        resolved.config
      ) {
        writeWidgetConfigCache(widgetConfigCacheKey, resolved.config);
      }
    }

    if (configSource) {
      logWidgetConfigSource(configSource);
    }

    // TODO find better way than throw error
    if (currentUser instanceof Response) {
      throw new Error(currentUser.statusText);
    }

    const isImpersonating = auth.isUserImpersonating(currentUser);
    console.log('isImpersonating', isImpersonating);
    let patientState;
    if (patientNumber) {
      patientState =
        patientResponse instanceof Response
          ? {
              ...setPatientError(patientResponse, patientNumber),
            }
          : {
              patient: setPatient(patientResponse),
            };
    } else {
      patientState = { patient: undefined };
    }
    let logger: LoggerType | undefined;
    if (this.props.logger) logger = this.props.logger;
    else {
      let envHosts: Record<string, unknown> | undefined;
      let loggingServiceHost: string | undefined;
      let loggingServicePath: string | undefined;
      let loggingServiceInterval: number | undefined;

      if (window.env.hosts)
        envHosts = window.env.hosts as Record<string, unknown>;

      if (envHosts) {
        loggingServiceHost = envHosts.loggingServiceHost as string;
        loggingServicePath = (envHosts.loggingServicePath as string) || '/log/';
        loggingServiceInterval =
          (envHosts.loggingServiceInterval as number) || 60 * 1000; // default 60 seconds
      }
      let hostString = loggingServiceHost;
      if (!!hostString && hostString.endsWith('/'))
        hostString = hostString.substring(0, hostString.length - 1);

      let pathString = loggingServicePath || '';
      if (pathString.startsWith('/')) pathString = pathString.substring(1);

      const loggingServiceUrl = `${hostString}/${pathString}`;
      logger = Logger({
        url: loggingServiceUrl,
        prefix: 'SmartChart',
        metricNamespacePrefix: 'SmartChart',
        logLevel: DEFAULT_LOG_LEVEL,
        flushInterval: loggingServiceInterval,
        patientInfo:
          (!(patientResponse instanceof Response) && patientResponse) ||
          undefined,
        userInfo:
          (!(currentUser instanceof Response) && currentUser) || undefined,
      });
      logger.addListeners();
    }
    // IDG MEETING
    let idgNarratives: any = null;

    if (isIdgMeetingComplete) {
      const idgNarrativesResponse = await this.pim.query(
        'idgmeetingnarrative_vw',
        {
          select: `narrative,rolegroupname,personlastname,personfirstname,review_id`,
          patientnumber: `eq.${patientNumber}`,
          meeting_id: `eq.${idgMeetingID}`,
        },
        'pimreader',
      );
      idgNarratives =
        idgNarrativesResponse instanceof Response
          ? null
          : idgNarrativesResponse;
    }

    const dashboardType: DashboardType = patientNumber
      ? patientDashboard
      : clinicalDashboard;

    this.setState({
      context: {
        currentUser,
        siteId: this.siteId,
        idgMeeting: isIdgMeeting
          ? {
              from,
              to,
              patientNumber,
              idgMeetingID,
              idgNarratives,
              idgReviewComplete: isIdgMeetingComplete,
              agency: this.agency,
              idgSourceKey: `${patientNumber}-${idgMeetingID}`,
            }
          : defaultIDGContent,
        keycloak: this.props.keycloak,
        locale: this.props.locale || DEFAULT_LOCALE,
        pim: this.pim,
        observation: this.observation,
        clearscripts: this.clearscripts,
        workflow: this.workflow,
        scheduler: this.scheduler,
        reporting: this.reporting,
        serviceConfig: this.serviceConfig,
        configUserId,
        setWidgetConfigLayoutDirty: this.setWidgetConfigLayoutDirty,
        onDashboardLayoutSaved: this.onDashboardLayoutSaved,
        agency: this.agency as string,
        isMummsAdmin,
        isImpersonating,
        onDashboardThemeChange: this.onDashboardThemeChange,
        dashboardType,
        config: widgetconfig,
        ...patientState,
        visitAttributes: [],
        logger,
      },
      theme: localStorage.getItem(THEME),
    });

    // Visit attributes are not required for the widget grid or initial dashboard
    // paint. Fetch after setState so getUserInfo / getPatientHeader /
    // getWidgetConfig stay on the critical path; HOPE/visit flows hydrate when
    // this resolves (context starts with visitAttributes: []).
    this.pim
      .getVisitAttributes(this.agency as string)
      .then((visitAttributesResponse) => {
        if (visitAttributesResponse instanceof Response) {
          return;
        }
        this.setState((prevState) =>
          prevState.context
            ? {
                ...prevState,
                context: {
                  ...prevState.context,
                  visitAttributes: visitAttributesResponse as mumms.VisitAttribute[],
                },
              }
            : prevState,
        );
      });

    if (cachedWidgetConfig && widgetConfigCacheKey) {
      this.refreshWidgetConfigInBackground(
        widgetConfigCacheKey,
        cachedWidgetConfig,
      );
    }
  };

  /**
   * Re-fetch widget config after a cache hit. Merges the single-dashboard GET
   * body into the cached {@link Config} so the other dashboard slot is preserved.
   */
  refreshWidgetConfigInBackground = (
    cacheKey: string,
    cachedConfig: Config,
  ) => {
    if (this.widgetConfigLayoutDirty) {
      this.pendingWidgetConfigBackgroundRefresh = { cacheKey, cachedConfig };
      console.warn(
        '[widget-config] deferring background refresh while layout is dirty',
      );
      return;
    }
    const configUserId = getKeycloakConfigUserId(this.props.keycloak);
    this.serviceConfig
      .getWidgetConfig(this.buildWidgetConfigParams(configUserId))
      .then((response) => {
        if (this.widgetConfigLayoutDirty) {
          this.pendingWidgetConfigBackgroundRefresh = {
            cacheKey,
            cachedConfig,
          };
          return;
        }
        if (response instanceof Response) {
          console.warn(
            '[widget-config] background refresh failed; keeping cache',
            response.status,
          );
          return;
        }
        const freshConfig = mergeWidgetConfigFromApi(cachedConfig, response);
        writeWidgetConfigCache(cacheKey, freshConfig);
        if (!areWidgetConfigsEqual(cachedConfig, freshConfig)) {
          logWidgetConfigSource('api');
          this.setState((prevState) =>
            prevState.context
              ? {
                  ...prevState,
                  context: {
                    ...prevState.context,
                    config: freshConfig,
                  },
                }
              : prevState,
          );
        }
      });
  };

  componentDidMount() {
    // form widget
    localStorage.removeItem(FORM_NAME);
    // overall theme of dashboard
    // dashboard sets the defaultTheme if none
    const theme = localStorage.getItem(THEME);
    !theme && localStorage.setItem(THEME, defaultTheme);
    this.getInitialData();
  }

  render() {
    // theme is saved in state and updated when changed
    const { context, theme } = this.state;
    const { embedded, locale = DEFAULT_LOCALE } = this.props;
    return (
      <Providers locale={locale} key={locale} theme={theme}>
        <BrowserRouter>
          <Content context={context} embedded={embedded} />
        </BrowserRouter>
      </Providers>
    );
  }
}

export default Dashboard;
